import { Ref, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { usePejerBase } from './stores/pejerData'

type Alias<T> = {
  [key in keyof T]:
    | {
        name: string
        toValue?: (s: string) => T[key]
      }
    | string
}
type ReverseAlias<T> = {
  [key: string]: keyof T
}

type Values = {
  [key: string]: string
}

function getObjectFromAlias<T>(query: Values, alias: Alias<T>, fallback: T, key?: string): T {
  const reverseAliasMap = {} as ReverseAlias<T>

  const aliasList = Object.entries(alias)
  aliasList.forEach(([aliasKey, _value]) => {
    const value = _value as Alias<T>[any]

    const name = typeof value === 'string' ? value : value.name

    reverseAliasMap[formatKey(name, key)] = aliasKey as keyof T
  })

  const result = {
    ...fallback,
  } as T

  if (!query) return result

  for (const [key, value] of Object.entries(query)) {
    const aliasObj = alias[reverseAliasMap[key]]
    if (!aliasObj) continue

    let resultingValue: unknown

    if (typeof fallback[reverseAliasMap[key]] === 'number') {
      resultingValue = parseInt(value)
    } else if (typeof fallback[reverseAliasMap[key]] === 'boolean') {
      resultingValue = value === 'true' ? true : false
    } else if (typeof fallback[reverseAliasMap[key]] === 'string') {
      resultingValue = value
    } else if (fallback[reverseAliasMap[key]] instanceof Date) {
      resultingValue = new Date(value)
    } else if (typeof aliasObj === 'object') {
      resultingValue = aliasObj.toValue ? aliasObj.toValue(value) : value
    } else {
      throw new Error('Unknown type')
    }

    result[reverseAliasMap[key]] = resultingValue as T[keyof T]
  }

  return result
}

function formatKey(param: string, key?: string) {
  return (key && key != '' ? key + '.' : '') + param
}

const queuedUpdates: Promise<any>[] = []

export default function useQueryParams<ResultingObject extends Record<string, unknown>>(
  defaultValues: ResultingObject,
  alias: Alias<ResultingObject> | string,
  options?: {
    key?: string
    formatter?: {
      stringify: (value: ResultingObject) => string
      parse: (value: any) => ResultingObject
    }
  }
) {
  const route = useRoute()
  const router = useRouter()

  const state = ref<ResultingObject>(
    typeof alias !== 'string'
      ? getObjectFromAlias<ResultingObject>(
          route?.query as Values,
          alias,
          defaultValues,
          options?.key
        )
      : route?.query[formatKey(alias, options?.key)]
        ? options?.formatter?.parse(route.query[formatKey(alias, options?.key)] as string) ||
          (JSON.parse(route.query[formatKey(alias, options?.key)] as string) as ResultingObject)
        : defaultValues
  ) as Ref<ResultingObject>

  const pejer = usePejerBase()
  async function updateParams(values: ResultingObject) {
    if (pejer.state.settings.disableQueryParams) return
    if (queuedUpdates.length > 0) await Promise.all(queuedUpdates)

    const filledQuery = {
      ...route.query,
    } as Record<string, string | undefined>

    if (typeof alias === 'string') {
      const stringifyFunction = options?.formatter?.stringify || JSON.stringify
      const stringifiedValues = stringifyFunction(values)
      if (stringifiedValues !== stringifyFunction(defaultValues)) {
        filledQuery[formatKey(alias, options?.key)] = stringifiedValues
      } else {
        delete filledQuery[formatKey(alias, options?.key)]
      }
    } else {
      for (const [key, value] of Object.entries(values)) {
        // @ts-ignore this is a valid key...
        const name = typeof alias[key] === 'string' ? alias[key] : alias[key].name
        if (value !== defaultValues[key]) {
          filledQuery[formatKey(name, options?.key)] = String(value)
        } else {
          delete filledQuery[formatKey(name, options?.key)]
        }
      }
    }

    // const currentQuery = new URLSearchParams(filledQuery)
    // for (const [key, value] of Object.entries(filledQuery)) {
    //   if (value === undefined) {
    //     currentQuery.delete(key)
    //   } else {
    //     currentQuery.set(key, value)
    //   }
    // }
    // history.replaceState({ ...history.state }, '', route.path + '?' + currentQuery.toString())

    // console.log('Filled query', filledQuery)
    const res = router.replace({
      query: filledQuery,
      state: { ...history.state },
      // state: { ...history.state, ['__NAVIGATION_REASON']: 'I' },
      // force: true,
    })
    const index = queuedUpdates.push(res) - 1
    await res
    if (index > -1) queuedUpdates.splice(index, 1)
  }

  return {
    state,
    updateParams,
  }
}
